import React from "react";
import { graphql, Link } from "gatsby";

import Page from "./../components/Page";
import SEO from "./../components/SEO";
import EmailSubscription from "./../components/EmailSubscription";
import AnimatedHeadline from "../utils/AnimatedHeadline";
import HomePageImageCarousel from "../components/HomePageImageCarousel";
import Title from "../components/Title";

import mghIcon from "../images/mgh-icon.svg";
import radicalIcon from "../images/radical-icon.svg";
import nihIcon from "../images/nih-icon.svg";
import iconPerformance from "../images/icon-performance.svg";
import iconWorkflows from "../images/icon-workflows.svg";
import iconFlexible from "../images/icon-flexible.svg";
import iconSlack from "../images/icon-slack.svg";
import iconOfficeHours from "../images/icon-office-hours.svg";
import iconSupport from "../images/icon-support.svg";

function ColumnContent({ header, body, linkText, link, href, className = "" }) {
  return (
    <div className={className}>
      <Title type="h1" className="mx-auto">
        {header}
      </Title>
      <p className="text-lg leading-normal mt-2 text-white">{body}</p>
      {link ? (
        <Link to={link}>
          <button className="flex items-center text-blue-bright mx-auto">
            {linkText}
            <span className="phone-size:right-arrow-ps lg:right-arrow-lg pl-1">
              &rarr;
            </span>
          </button>
        </Link>
      ) : (
        <a href={href} target="_blank" rel="noopener noreferrer">
          <button className="flex items-center text-blue-bright mx-auto">
            {linkText}
            <span className="phone-size:right-arrow-ps lg:right-arrow-lg pl-1">
              &rarr;
            </span>
          </button>
        </a>
      )}
    </div>
  );
}

function IndexPage({ data }) {
  const imagesInfo = data.allHomepageScreenshotsJson.nodes;

  return (
    <Page>
      <SEO title="Open Health Imaging Foundation" />
      <div className="text-center bg-blue-800 mt-4">
        {/* INTRO */}
        <section className="mx-auto px-0 py-4 md:py-0 lg:py-12">
          <div className="grid lg:grid-cols-[45fr_55fr] items-start gap-2 lg:gap-8 w-full">
            {/* Left: Text + CTAs */}
            <div className="text-left min-w-0">
              <h1 className="text-[#5acce6] text-[28px] sm:text-[32px] md:text-[37px] font-semibold tracking-tight">
                Extensible Image Viewer
              </h1>
              <p className="text-white text-[22px] md:text-[25px] xl:text-[28px] font-light">Open-source. Web-based. Free to use.</p>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <a
                  href="https://viewer.ohif.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-9 px-5 rounded bg-[#358cfd]/60 text-white text-[16px] md:text-[17px] hover:bg-[#358cfd]/70 transition-colors"
                >
                  Try it now
                </a>
                <Link
                  to="/features"
                  className="inline-flex items-center justify-center h-9 px-5 rounded bg-[#358cfd]/40 text-white text-[16px] md:text-[17px] hover:bg-[#358cfd]/50 transition-colors"
                >
                  Features
                </Link>
              </div>

              <p className="text-white mt-3 md:mt-6 text-[18px] sm:text-[19px] md:text-[19px] xl:text-[21px] leading-[28px] md:leading-[30px] xl:leading-[32px]">
                The OHIF Viewer is a versatile, lightweight tool for medical imaging visualization,
                analysis, and custom workflows that runs entirely in your browser.
              </p>

              {/* About OHIF */}
              <div className="mt-4">
                {/* Horizontal divider */}

                <p className="text-[#7bb2ce] text-[15px] md:text-[16px] leading-[22px] md:leading-[24px]">
                  OHIF is a program of Massachusetts General Hospital, developed in
                  collaboration with Radical Imaging to advance the open-source
                  medical imaging community.{" "}
                  <Link to="/about" className="text-[#358cfd] font-semibold border-b border-transparent hover:border-current">
                    Learn more
                  </Link>
                </p>

                {/* Organization rows */}
                <div className="mt-5 space-y-2.5">
                  {/* Row 1: MGH */}
                  <div className="flex items-start sm:items-center gap-1.5">
                    <a href="https://www.massgeneral.org/" target="_blank" rel="noopener noreferrer" className="w-[25px] flex-shrink-0 mt-1 sm:mt-0">
                      <img src={mghIcon} alt="" className="w-[17px] h-[25px]" />
                    </a>
                    <p className="leading-[22px] md:leading-[23px]">
                      <a href="https://www.massgeneral.org/" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[15px] md:text-[16px] font-semibold border-b border-transparent hover:border-current">Massachusetts General Hospital</a>
                      {" "}<span className="text-[#7bb2ce] text-[13px] md:text-[14px]">Project Management &amp; UX&nbsp;Design</span>
                    </p>
                  </div>

                  {/* Row 2: Radical Imaging */}
                  <div className="flex items-start sm:items-center gap-1.5">
                    <a href="https://radicalimaging.com/" target="_blank" rel="noopener noreferrer" className="w-[25px] flex-shrink-0 mt-1 sm:mt-0">
                      <img src={radicalIcon} alt="" className="w-[17px] h-[17px]" />
                    </a>
                    <p className="leading-[22px] md:leading-[23px]">
                      <a href="https://radicalimaging.com/" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[15px] md:text-[16px] font-semibold border-b border-transparent hover:border-current">Radical Imaging</a>
                      {" "}<span className="text-[#7bb2ce] text-[13px] md:text-[14px]">Architecture and Software Development</span>
                    </p>
                  </div>

                  {/* Row 3: NIH */}
                  <div className="flex items-start sm:items-center gap-1.5">
                    <a href="https://www.nih.gov/" target="_blank" rel="noopener noreferrer" className="w-[25px] flex-shrink-0 mt-0.5 sm:mt-0">
                      <img src={nihIcon} alt="" className="w-[26px] h-[16px]" />
                    </a>
                    <p className="leading-[22px] md:leading-[23px]">
                      <a href="https://www.nih.gov/" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[15px] md:text-[16px] font-semibold border-b border-transparent hover:border-current">National Institutes of Health</a>
                      {" "}<span className="text-[#7bb2ce] text-[13px] md:text-[14px]">Funding through Grant Support</span>
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Right: Image carousel */}
            <div className="w-full min-w-0">
              <HomePageImageCarousel imagesInfo={imagesInfo} />
            </div>
          </div>
        </section>

        {/* SECTION 1 */}
        <section className="mx-auto mt-8 md:mt-10">
          {/* Section header */}
          <div className="text-center">
            <h2 className="text-[#5acce6] text-[36px] leading-[45px] font-semibold">Learn more</h2>
            <p className="text-white text-[36px] leading-[45px] font-light">explore the imaging platform</p>
          </div>

          {/* Three equal columns */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-left">
            {/* Column 1 */}
            <div className="relative pl-8">
              {/* Vertical accent line - extends full column height */}
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
              <div className="flex items-center justify-start mb-4">
                <img
                  src={iconPerformance}
                  alt="Performance icon"
                  className="h-11 w-11"
                />
              </div>
              <div className="md:max-w-[359px] mx-auto md:mx-0">
                <h3>
                  <Link to="/features#performance" className="text-[#358cfd] text-[21px] leading-normal border-b border-transparent hover:border-current">Performance focused web app</Link>
                </h3>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  GPU-accelerated, advanced visualization in your browser, optimized for speed and usability
                </p>
              </div>
            </div>

            {/* Column 2 */}
            <div className="relative pl-8">
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
              <div className="flex items-center justify-start mb-4">
                <img
                  src={iconWorkflows}
                  alt="Workflows icon"
                  className="h-11 w-11"
                />
              </div>
              <div className="md:max-w-[359px] mx-auto md:mx-0">
                <h3>
                  <Link to="/features#workflow" className="text-[#358cfd] text-[21px] leading-normal border-b border-transparent hover:border-current">Custom workflows &amp; Experience</Link>
                </h3>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  Efficient and streamlined workflows including segmentation, custom views, and more
                </p>
              </div>
            </div>

            {/* Column 3 */}
            <div className="relative pl-8">
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
              <div className="flex items-center justify-start mb-4">
                <img
                  src={iconFlexible}
                  alt="Flexible icon"
                  className="h-11 w-11"
                />
              </div>
              <div className="md:max-w-[359px] mx-auto md:mx-0">
                <h3>
                  <Link to="/features#flexible" className="text-[#358cfd] text-[21px] leading-normal border-b border-transparent hover:border-current">Flexible &amp; extensible framework</Link>
                </h3>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  Open platform designed for flexibility, easy integration, and robust community support
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Divider between Section 1 and Section 2 */}
        <div className="mx-auto mt-28 md:mt-32 border-t border-[#2A2B4A]" />

        {/* SECTION 2 */}
        <section className="mx-auto mt-32 md:mt-28">
          {/* Section header */}
          <div className="text-center">
            <h2 className="text-[#5acce6] text-[36px] leading-[44px] font-semibold">Collaborate and Innovate</h2>
            <p className="text-white text-[36px] leading-[44px] font-light">with peers and experts</p>
          </div>

          {/* Three equal columns */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-left">
            {/* Column 1: Slack community */}
            <div className="relative pl-8">
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
              <div className="flex items-center justify-start mb-4">
                <img
                  src={iconSlack}
                  alt="Slack icon"
                  className="h-12 w-12"
                />
              </div>
              <div className="md:max-w-[359px] mx-auto md:mx-0">
                <h3>
                  <a href="https://join.slack.com/t/cornerstonejs/shared_invite/zt-3108kuc8m-ON1zP3nWsrgPNv9mM0E5fw" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[21px] leading-normal border-b border-transparent hover:border-current">Slack community</a>
                </h3>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  Join our Slack community to share ideas, get support, and contribute to OHIF Viewer
                </p>
              </div>
            </div>

            {/* Column 2: Office Hours */}
            <div className="relative pl-8">
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
              <div className="flex items-center justify-start mb-4">
                <img
                  src={iconOfficeHours}
                  alt="Office hours icon"
                  className="h-12 w-12"
                />
              </div>
              <div className="md:max-w-[359px] mx-auto md:mx-0">
                <h3>
                  <a href="https://github.com/OHIF/OHIF-Office-Hours" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[21px] leading-normal border-b border-transparent hover:border-current">Office Hours</a>
                </h3>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  Bi-weekly sessions to ask questions, share feedback, get personalized support directly from experts
                </p>
              </div>
            </div>

            {/* Column 3: Expert Support for OHIF */}
            <div className="relative pl-8">
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
              <div className="flex items-center justify-start mb-4">
                <img
                  src={iconSupport}
                  alt="Support icon"
                  className="h-12 w-12"
                />
              </div>
              <div className="md:max-w-[359px] mx-auto md:mx-0">
                <h3>
                  <Link to="/get-support" className="text-[#358cfd] text-[21px] leading-normal border-b border-transparent hover:border-current">Expert Support for OHIF</Link>
                </h3>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  Get expert support from OHIF's core engineers to customize OHIF, integrate AI, or upgrade to the latest version
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Divider between Section 2 and Section 3 */}
        <div className="mx-auto mt-28 md:mt-28 border-t border-[#2A2B4A]" />

        {/* PRINCIPAL ORGANIZATIONS */}
        <section className="mx-auto mt-28 md:mt-28">
          {/* Section header */}
          <div className="text-center">
            <h2 className="text-[#5acce6] text-[36px] leading-[44px] font-semibold">Principal organizations</h2>
            <p className="text-white text-[36px] leading-[44px] font-light">Founding collaborators and funding organizations</p>
          </div>

          {/* Founding members */}
          <div className="mt-12">
            <h3 className="text-[#7bb2ce] text-[27px] leading-[32px] text-center mb-8">Founding members</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 text-left">
              {/* MGH */}
              <div className="relative pl-6">
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
                <h4>
                  <a href="https://www.massgeneral.org/" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[22px] leading-normal border-b border-transparent hover:border-current">
                    Massachusetts General Hospital (MGH)
                  </a>
                </h4>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  OHIF is a program of MGH, a non-profit medical center, which provides Project Management, UX design, and software testing
                </p>
              </div>

              {/* Radical Imaging */}
              <div className="relative pl-6">
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
                <h4>
                  <a href="https://radicalimaging.com/" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[22px] leading-normal border-b border-transparent hover:border-current">
                    Radical Imaging
                  </a>
                </h4>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  Leads OHIF architecture and software development; offers support to third parties seeking experts to extend or customize the platform
                </p>
              </div>
            </div>
          </div>

          {/* Supported with grants from */}
          <div className="mt-16">
            <h3 className="text-[#7bb2ce] text-[27px] leading-[32px] text-center mb-8">Supported with grants from</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 text-left">
              {/* NIH National Cancer Institute ITCR */}
              <div className="relative pl-6">
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
                <h4>
                  <a href="https://www.nih.gov/" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[22px] leading-normal border-b border-transparent hover:border-current">
                    NIH National Cancer Institute ITCR
                  </a>
                </h4>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  The NIH/NCI Informatics Technology for Cancer Research (ITCR) program has funded OHIF through grants since its start (2015-20, 2023-28)
                </p>
              </div>

              {/* Chan Zuckerberg Initiative */}
              <div className="relative pl-6">
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#358cfd]/30" />
                <h4>
                  <a href="https://chanzuckerberg.com/" target="_blank" rel="noopener noreferrer" className="text-[#358cfd] text-[22px] leading-normal border-b border-transparent hover:border-current">
                    Chan Zuckerberg Initiative (CZI)
                  </a>
                </h4>
                <p className="text-white text-[14px] leading-[24px] mt-2">
                  OHIF received several grants (2020-2023) from the CZI Essential Open-Source Software for Science (EOSS) program
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Divider after Principal Organizations */}
        <div className="mx-auto mt-28 md:mt-28 border-t border-[#2A2B4A]" />

        {/* NewsLetter */}
        <div className="flex w-full mt-16 md:mt-24 mx-auto mb-24 flex-col flex-grow md:flex-row px-4 md:px-6">
          <div className="text-left md:w-1/2 md:pr-10">
            <Title type="h1">Stay up to date</Title>
            <p className="text-white mt-6">
              Subscribe to the Newsletter to stay up-to-date with new releases,
              functionality, and contributions from the community.
            </p>
          </div>
          <div className="md:w-1/2 w-full mt-4 md:mt-0">
            <div>
              <EmailSubscription />
            </div>
            <div className="text-white text-left sm:-mt-2">
              See our{" "}
              <Link to="/news" className="text-blue-100 border-b border-transparent hover:border-current">previous newsletters</Link>
              .
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

export const query = graphql`
  {
    allHomepageScreenshotsJson(
      sort: {fields: order, order: ASC}
      filter: {hidden: {ne: true}}
    ) {
      nodes {
        name
        id
        image {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`;

export default IndexPage;
